import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { can } from "../../auth/can.ts";
import { Partner } from "../../models/Partner";
import { getCurrencyFormat } from "../../utils/index.ts";
import {
  setCurrentUser,
  setDataToRemove,
  setDefaultSort,
  setFetch,
  setMessageModal,
  setModalChildren,
  setModalCredentials,
  setModalDelete,
  setModalTitle,
  setModalSize,
  setPage,
  setShow,
  setSortBy,
} from "../../store/slices/colaboradoresSlice.ts";
import { setShowModal as setShowModalSlice } from "../../store/slices/colaboradoresSlice.ts";
import moment from "moment";
import "moment/locale/es";
import { useNavigate } from "react-router-dom";
import EmptyImage from "../../assets/img/ic_content.svg";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import ChangePositionModal from "../../components/colaboradores/ChangePositionModal.tsx";
import TransportHistoryModal from "../../components/colaboradores/TransportHistoryModal.tsx";
import UpdateSalary from "../../components/profile/UpdateSalary.tsx";
import { notification } from "../../utils/Notifications.tsx";
import { deletePartner, updateBanDate } from "../../services/partner.service.ts";
import { Tooltip } from '@mui/material';
import { fixedHeadStyle, fixedStyle } from "../../utils/constanst.js";
import BossProfileAvatar from "../../components/direct-boss/BossProfileAvatar.tsx";
moment.locale("es");

const PartnerNameCell = ({ colaborador }: { colaborador: Partner }) => {
  const fullName = toCamelCase(
    `${colaborador.person.firstName} ${colaborador.person.lastName} ${colaborador.person.secondLastName}`
  );

  const nameContent = (
    <>
      {isTodayBirthday(colaborador.person.birthdate) && (
        <i className="fa-solid fa-cake me-2" style={{ color: "#FFB22C" }} />
      )}
      {fullName}
      {colaborador.badges.length > 0 && (
        <Tooltip
          title={
            <div>
              {colaborador.badges.map((badge) => (
                <div key={badge.id}>
                  <i
                    className="fa-solid fa-medal me-2"
                    style={{ color: "#FFD700" }}
                  />
                  {badge.title}
                </div>
              ))}
            </div>
          }
          arrow
        >
          <i
            className="fa-solid fa-medal ms-2"
            style={{ color: "#FFD700" }}
          />
        </Tooltip>
      )}
    </>
  );

  const inner = isTodayBirthday(colaborador.person.birthdate) ? (
    <Tooltip title="Cumpleañero" arrow>
      <div>{nameContent}</div>
    </Tooltip>
  ) : (
    <div>{nameContent}</div>
  );

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.625rem",
        minWidth: 0,
      }}
    >
      <BossProfileAvatar
        partnerId={colaborador.id}
        curp={colaborador.person.curp}
        firstName={colaborador.person.firstName}
        lastName={colaborador.person.lastName}
        size={36}
      />
      <div style={{ minWidth: 0 }}>{inner}</div>
    </div>
  );
};

const isTodayBirthday = (date: string) => {
  const birthday = moment(date).format("MM-DD");
  const currentYearBirthday = moment(birthday).year(moment().year());
  return currentYearBirthday.isSame(moment(), "day");
};

const TableColaboradores = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { colaboradores, sortBy, defaultSort, fetch } = useSelector(
    (state: RootState) => state.colaboradores
  );
  const { user } = useSelector((state: RootState) => state.auth);
  const canEditPartner = can("partners.edit", user);
  const canViewSalary = can("partners.view_salary", user);
  const canEditSalary = can("partners.edit_salary", user);
  const [editingBanDateId, setEditingBanDateId] = React.useState<number | null>(null);
  const [banDateValue, setBanDateValue] = React.useState<string>("");
  const [loadingBanDate, setLoadingBanDate] = React.useState<boolean>(false);
  const [loadingSalary, setLoadingSalary] = React.useState<boolean>(false);

  const moneyTextStyle: React.CSSProperties = {
    fontVariantNumeric: "tabular-nums",
    fontWeight: 600,
    fontFamily:
      'ui-monospace, "SF Mono", Consolas, "Liberation Mono", monospace',
    letterSpacing: "0.02em",
  };

  const dateTextStyle: React.CSSProperties = {
    fontVariantNumeric: "tabular-nums",
    fontWeight: 500,
  };

  const actionButtonStyle: React.CSSProperties = {
    borderRadius: "999px",
    width: "34px",
    height: "34px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    margin: "0 0.15rem",
  };

  const getStatusMeta = (colaborador: Partner) => {
    const reason = (colaborador.reasonUnsubscribe || "").toLowerCase();
    const hasProgramed = Boolean(colaborador.programedUnsubscribed);
    const programedCompleted = colaborador.programedUnsubscribed?.completed === true;
    const programedPending = hasProgramed && !programedCompleted;

    let text = colaborador.partnerStatus.name;
    if (programedPending) {
      text = "Baja programada";
    } else if (
      colaborador.partnerStatus.name === "Suspendido" &&
      reason.includes("baja por aprobar")
    ) {
      text = "Baja por aprobar";
    }

    // Colores suaves (fondo claro + texto oscuro) como en TableSolicitudes
    const chipColors: { main: string; light: string } =
      text === "Activo"
        ? { main: "#166534", light: "#bbf7d0" }
        : text === "Suspendido"
        ? { main: "#92400e", light: "#fef3c7" }
        : text === "Baja programada" || text === "Baja por aprobar"
        ? { main: "#92400e", light: "#fef3c7" }
        : { main: "#b91c1c", light: "#fee2e2" };

    return { text, chipColors, programedPending };
  };

  const getStatusBadgeStyle = (meta: { text: string; chipColors: { main: string; light: string } }): React.CSSProperties => ({
    backgroundColor: meta.chipColors.light,
    color: meta.chipColors.main,
    padding: "0.25rem 0.5rem",
    borderRadius: "999px",
    fontSize: "12px",
    display: "inline-flex",
    alignItems: "center",
  });

  const formatDate = (date?: string | null) => {
    if (!date) return "Sin fecha";
    // Ej: Lun 20 feb 2026
    return moment(date).format("ddd DD MMM YYYY");
  };

  const formatDateWithoutTime = (date?: string | null) => {
    if (!date) return "Sin fecha";
    return moment(date).format("ddd DD MMM YYYY");
  };


  const getIconSort = () => {
    return (
      <i
        className={`fa-solid fa-sort-${defaultSort === "asc" ? "up" : "down"}`}
      />
    );
  };

  const setSort = (sort: string) => {
    const sortOrder =
      sortBy !== sort ? "asc" : defaultSort === "asc" ? "desc" : "asc";

    dispatch(setSortBy(sort));
    dispatch(setPage(0));

    dispatch(setDefaultSort(sortOrder));
  };

  const handleDeletePartner = async (id: number) => {
    try {
      await deletePartner(id);
      dispatch(setFetch(!fetch));
      notification("Eliminado", "Colaborador eliminado con éxito", "success");
    }
    catch (error) {
      notification("Error", "Error al eliminar el colaborador", "danger");
    }
  }

  const handleEditBanDate = (colaborador: Partner) => {
    if (colaborador.partnerStatus.name !== "Baja") return;
    if (!canEditPartner) return;
    setEditingBanDateId(colaborador.id);
    setBanDateValue(
      colaborador.banDate
        ? moment(colaborador.banDate).format("YYYY-MM-DD")
        : ""
    );
  };

  const handleSaveBanDate = async (id: number) => {
    if (!banDateValue) {
      notification("Error", "La fecha de baja es requerida", "danger");
      return;
    }

    setLoadingBanDate(true);
    try {
      await updateBanDate(id, banDateValue);
      dispatch(setFetch(!fetch));
      setEditingBanDateId(null);
      setBanDateValue("");
      notification("Éxito", "Fecha de baja actualizada correctamente", "success");
    } catch (error: any) {
      notification(
        "Error",
        error?.response?.data?.message || "Error al actualizar la fecha de baja",
        "danger"
      );
    } finally {
      setLoadingBanDate(false);
    }
  };

  const handleCancelEditBanDate = () => {
    setEditingBanDateId(null);
    setBanDateValue("");
  };

  const handleEditSalary = (colaborador: Partner) => {
    if (!canEditSalary) return;
    
    dispatch(setShowModalSlice(true));
    dispatch(
      setModalChildren(
        <UpdateSalary
          closeFn={() => dispatch(setShowModalSlice(false))}
          refreshFn={() => {
            dispatch(setFetch(!fetch));
          }}
          setLoading={(value) => setLoadingSalary(value)}
          dataSalary={colaborador}
        />
      )
    );
    dispatch(
      setModalTitle(
        `Actualizar salario de ${toCamelCase(
          `${colaborador.person.firstName} ${colaborador.person.lastName} ${colaborador.person.secondLastName}`
        )}`
      )
    );
  };

  const thStyle: React.CSSProperties = {
    padding: "0.75rem 1rem",
    fontSize: "11px", fontWeight: 700,
    textTransform: "uppercase", letterSpacing: "0.06em",
    color: "#6b7280", background: "#f8fafc",
    borderBottom: "2px solid #e5e7eb",
    cursor: "pointer", whiteSpace: "nowrap",
  };

  const thCenterStyle: React.CSSProperties = {
    ...thStyle,
    textAlign: "center",
  };

  const tdCenterStyle: React.CSSProperties = {
    padding: "0.65rem 1rem",
    fontSize: "13px",
    borderBottom: "1px solid #f3f4f6",
    verticalAlign: "middle",
    textAlign: "center",
  };

  return (
    <div className="mb-3">
      <div
        className="table-responsive"
        style={{
          overflowY: "auto",
          height: "500px",
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
        }}
      >
        <table className="table table-panel text-nowrap align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
        <thead>
          <tr style={{ position: "sticky", top: 0, zIndex: 1 }}>
            <th style={{ ...thStyle, ...fixedStyle as React.CSSProperties }} onClick={() => setSort("id")}>
              Id {sortBy === "id" && getIconSort()}
            </th>
            <th style={thStyle} onClick={() => setSort("name")}>
              Nombre completo {sortBy === "name" && getIconSort()}
            </th>
            <th style={thCenterStyle} onClick={() => setSort("phone")}>
              Celular {sortBy === "phone" && getIconSort()}
            </th>
            <th style={thStyle} onClick={() => setSort("position")}>
              Puesto {sortBy === "position" && getIconSort()}
            </th>
            <th style={thStyle} onClick={() => setSort("shift")}>
              Turno {sortBy === "shift" && getIconSort()}
            </th>
            <th style={thCenterStyle} onClick={() => setSort("branch")}>
              Sucursal {sortBy === "branch" && getIconSort()}
            </th>
            <th style={thCenterStyle} onClick={() => setSort("status")}>
              Status {sortBy === "status" && getIconSort()}
            </th>
            <th style={thCenterStyle} onClick={() => setSort("bandate")}>
              Fecha de baja {sortBy === "bandate" && getIconSort()}
            </th>
            <th style={thCenterStyle} onClick={() => setSort("entrydate")}>
              Fecha de entrada {sortBy === "entrydate" && getIconSort()}
            </th>
            {canViewSalary && (
              <th style={thStyle} onClick={() => setSort("salary")}>
                Salario {sortBy === "salary" && getIconSort()}
              </th>
            )}
            <th style={{ ...thStyle, textAlign: "right", cursor: "default" }}>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {colaboradores?.length > 0 ? (
            colaboradores.map((colaborador: Partner, index) => (
              <tr
                key={index}
                style={{ background: index % 2 === 0 ? "#ffffff" : "#fafafa", transition: "background 0.1s" }}
                onMouseEnter={e => (e.currentTarget.style.background = "#f0f9ff")}
                onMouseLeave={e => (e.currentTarget.style.background = index % 2 === 0 ? "#ffffff" : "#fafafa")}
              >
                <td style={{ ...fixedStyle as React.CSSProperties, padding: "0.65rem 1rem", fontSize: "13px", color: "#6b7280", fontWeight: 600, borderBottom: "1px solid #f3f4f6", verticalAlign: "middle" }}>{colaborador.id}</td>
                <td
                  style={{
                    padding: "0.65rem 1rem",
                    fontSize: "13px",
                    borderBottom: "1px solid #f3f4f6",
                    verticalAlign: "middle",
                  }}
                >
                  <PartnerNameCell colaborador={colaborador} />
                </td>
                <td style={tdCenterStyle}>{colaborador.person.phone}</td>
                <td>{colaborador.candidate?.vacancy?.position.name}</td>
                <td>{colaborador.candidate?.shift?.name}</td>
                <td style={tdCenterStyle}>{colaborador.candidate?.branch?.name}</td>
                {/* <td>{item.contacto}</td> ???? */}
                {/* <td>{colaborador.partnerStatus.name}</td> */}
                <td style={tdCenterStyle}>
                  {(() => {
                    const meta = getStatusMeta(colaborador);
                    const badge = (
                      <span style={getStatusBadgeStyle(meta)}>
                        {meta.programedPending && (
                          <i className="fa-solid fa-clock me-2"></i>
                        )}
                        {meta.text}
                      </span>
                    );

                    if (meta.programedPending && colaborador.programedUnsubscribed) {
                      return (
                        <Tooltip
                          title={`Baja programada para el ${moment(colaborador.programedUnsubscribed.banDate).format('DD-MM-YYYY')}`}
                          arrow
                        >
                          {badge}
                        </Tooltip>
                      );
                    }

                    return badge;
                  })()}
                </td>
                {/* <td>
                  <div
                    style={{
                      backgroundColor:
                        colaborador.temporarilySuspend
                          ? "#FFB22C"
                          : "#4ac792",
                      color: "white",
                      padding: "3px 7px",
                      borderRadius: "10px",
                      display: "inline-block",
                      marginInline: "auto",
                    }}
                  > 
                    {colaborador.temporarilySuspend ? "Baja temporal" : "Activo"}
                  </div>
                </td> */}
                <td style={tdCenterStyle}>
                  {editingBanDateId === colaborador.id ? (
                    <div style={{ display: "flex", gap: "4px", alignItems: "center", justifyContent: "center" }}>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={banDateValue}
                        onChange={(e) => setBanDateValue(e.target.value)}
                        style={{ width: "150px", fontSize: "12px" }}
                        disabled={loadingBanDate}
                      />
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => handleSaveBanDate(colaborador.id)}
                        disabled={loadingBanDate}
                        style={{ padding: "2px 8px", fontSize: "11px" }}
                      >
                        <i className="fa-solid fa-check"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={handleCancelEditBanDate}
                        disabled={loadingBanDate}
                        style={{ padding: "2px 8px", fontSize: "11px" }}
                      >
                        <i className="fa-solid fa-times"></i>
                      </button>
                    </div>
                  ) : (
                    <div
                      style={{
                        cursor:
                          colaborador.partnerStatus.name === "Baja" &&
                          canEditPartner
                            ? "pointer"
                            : "default",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                      }}
                      onClick={() =>
                        colaborador.partnerStatus.name === "Baja" &&
                        canEditPartner &&
                        handleEditBanDate(colaborador)
                      }
                      title={
                        colaborador.partnerStatus.name === "Baja" &&
                        canEditPartner
                          ? "Haz clic para editar"
                          : ""
                      }
                    >
                      {colaborador.partnerStatus.name === "Baja" ? (
                        <>
                          <span style={dateTextStyle}>
                            {formatDate(colaborador.banDate)}
                          </span>
                          {canEditPartner && (
                            <i
                              className="fa-solid fa-edit"
                              style={{
                                fontSize: "11px",
                                color: "#6c757d",
                                opacity: 0.7,
                              }}
                            />
                          )}
                        </>
                      ) : (
                        <span
                          style={{
                            ...dateTextStyle,
                            color: "#6c757d",
                            fontStyle: "italic",
                            fontWeight: 400,
                          }}
                        >
                          No aplica
                        </span>
                      )}
                    </div>
                  )}
                </td>
                <td style={tdCenterStyle}>
                  {colaborador.originalRecruitmentDate ? (
                    <span style={dateTextStyle}>
                      {formatDateWithoutTime(
                        colaborador.originalRecruitmentDate
                      )}
                    </span>
                  ) : (
                    <span
                      style={{
                        ...dateTextStyle,
                        color: "#6c757d",
                        fontStyle: "italic",
                        fontWeight: 400,
                      }}
                    >
                      Cargando
                    </span>
                  )}
                </td>
                {/* {!user.branch && ( */}
                {canViewSalary && (
                  <td>
                    <div
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                      onClick={() => handleEditSalary(colaborador)}
                      title="Haz clic para editar el salario"
                    >
                      <span style={moneyTextStyle}>
                        {getCurrencyFormat(colaborador?.candidate?.monthlySalary)}
                      </span>
                      <i
                        className="fa-solid fa-edit"
                        style={{ fontSize: "11px", color: "#6c757d", opacity: 0.7 }}
                      />
                    </div>
                  </td>
                )}
                <td>
                  {colaborador.partnerStatus.name === "Baja" && (
                    <Tooltip title={`Reactivar`} arrow>
                      <a
                        className="btn btn-azul"
                        style={actionButtonStyle}
                        onClick={() => {
                          dispatch(setShow(true));
                          dispatch(
                            setCurrentUser({
                              id: colaborador.id,
                              name: `${colaborador.person.firstName} ${colaborador.person.lastName} ${colaborador.person.secondLastName}`,
                              curp: colaborador.person.curp,
                            })
                          );
                        }}
                      >
                        <i className="fa-solid fa-check"></i>
                      </a>
                    </Tooltip>
                  )}
                  {colaborador.partnerStatus.name !== "Baja" && (
                    <Tooltip title={`Dar de baja`} arrow>
                      <a
                        className="btn btn-rojo"
                        style={actionButtonStyle}
                        onClick={() => {
                          dispatch(setModalDelete(true));
                          dispatch(
                            setDataToRemove({
                              id: colaborador.id,
                              name: `${colaborador.person.firstName} ${colaborador.person.lastName} ${colaborador.person.secondLastName}`,
                              programed: colaborador.programedUnsubscribed,
                            })
                          );
                        }}
                      >
                        <span className="d-none d-sm-inline"></span>
                        <i className="fa fa-ban"></i>
                      </a>
                    </Tooltip>
                  )}
                  <Tooltip title={`Credenciales`} arrow>
                    <a
                      className="btn btn-amarillo"
                      style={actionButtonStyle}
                      onClick={() => {
                        dispatch(setModalCredentials(true));
                        dispatch(
                          setCurrentUser({
                            id: colaborador.id,
                            name: `${colaborador.person.firstName} ${colaborador.person.lastName} ${colaborador.person.secondLastName}`,
                            curp: colaborador.person.curp,
                          })
                        );
                      }}
                    >
                      <i className="fa-solid fa-key"></i>
                    </a>
                  </Tooltip>
                  <Tooltip title={`Editar mensaje`} arrow>
                    <a
                      className="btn btn-verde"
                      style={actionButtonStyle}
                      onClick={() => {
                        dispatch(setMessageModal(true));
                        dispatch(
                          setCurrentUser({
                            id: colaborador.id,
                            name: `${colaborador.person.firstName} ${colaborador.person.lastName} ${colaborador.person.secondLastName}`,
                            curp: colaborador.person.curp,
                          })
                        );
                      }}
                    >
                      <i className="fa-solid fa-message"></i>
                    </a>
                  </Tooltip>
                  <Tooltip title={`Detalles`} arrow>
                    <a
                      className="btn btn-azul"
                      style={actionButtonStyle}
                      onClick={() =>
                        navigate(`/colaboradores/detalles/${colaborador.id}`)
                      }
                    >
                      <i className="fa-solid fa-circle-info"></i>
                    </a>
                  </Tooltip>
                  <Tooltip title={`Asistencias`} arrow>
                    <a
                      className="btn btn-amarillo"
                      style={actionButtonStyle}
                      onClick={() => navigate(`/sucursales/asistencias/colaborador/${colaborador.id}`)}
                    >
                      <i className="fa-solid fa-list-check"></i>
                    </a>
                  </Tooltip>
                  <Tooltip title={`Cambiar de puesto`} arrow>
                    <a
                      href="#/"
                      className="btn btn-secondary"
                      style={actionButtonStyle}
                      onClick={() => {
                        dispatch(setShowModalSlice(true));
                        dispatch(
                          setModalChildren(
                            <ChangePositionModal
                              closeFn={() => dispatch(setShowModalSlice(false))}
                              partner={colaborador}
                            />
                          )
                        );
                        dispatch(
                          setModalTitle(
                            `Cambiar de puesto a ${toCamelCase(
                              `${colaborador.person.firstName} ${colaborador.person.lastName} ${colaborador.person.secondLastName}`
                            )}`
                          )
                        );
                      }}
                    >
                      <i className="fa-solid fa-signs-post"></i>
                    </a>
                  </Tooltip>
                  <Tooltip title={`Historial de transporte`} arrow>
                    <a
                      href="#/"
                      className="btn btn-info"
                      style={actionButtonStyle}
                      onClick={() => {
                        dispatch(setModalSize("xl"));
                        dispatch(setShowModalSlice(true));
                        dispatch(
                          setModalChildren(
                            <TransportHistoryModal
                              partner={colaborador}
                              closeFn={() => dispatch(setShowModalSlice(false))}
                            />
                          )
                        );
                        dispatch(
                          setModalTitle(
                            `Transporte — ${toCamelCase(
                              `${colaborador.person.firstName} ${colaborador.person.lastName} ${colaborador.person.secondLastName}`
                            )}`
                          )
                        );
                      }}
                    >
                      <i className="fa-solid fa-bus"></i>
                    </a>
                  </Tooltip>
                  {/* {<a
                    className="btn btn-danger m-2"
                    onClick={() => handleDeletePartner(colaborador.id)}
                  >
                    borrar
                  </a> } */}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={11}>
                <div style={{ padding: "3rem 1rem", textAlign: "center" }}>
                  <img src={EmptyImage} alt="" style={{ width: 140, opacity: 0.6, marginBottom: "1rem" }} />
                  <p style={{ fontWeight: 600, color: "#374151", margin: "0 0 0.25rem" }}>
                    Sin colaboradores
                  </p>
                  <p style={{ color: "#9ca3af", fontSize: 13, margin: 0 }}>
                    No se encontraron registros con los filtros aplicados
                  </p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
        </table>
      </div>
    </div>
  );
};

export default TableColaboradores;
